import {PropsWithChildren, createContext, useContext} from "react";

/**
 * The Pricing hierarchy rule, as the offering form enforces it.
 *
 * An Add-on is an optional extra priced separately and added to the product total. Under
 * Pay What You Want the customer names one amount, so there is no total to add to — the
 * two cannot coexist. The Add-ons section is therefore locked rather than hidden: a
 * section that vanishes when a radio is clicked reads as a bug.
 *
 * The copy lives here, next to the gate, so the locked toggle and the discard warning
 * cannot drift apart.
 */
export const ADD_ONS_UNAVAILABLE_UNDER_PWYW =
  "Add-ons are unavailable with Pay what you want, because your customer names one amount rather than adding to a total.";

export const ADD_ON_DISCARD_TITLE = "Remove your add-ons?";

/**
 * Names how much the coach is about to lose. Only ever shown when there is something to
 * lose — an offering with no Add-ons switches without a word.
 */
export function addOnDiscardWarning(addOnCount: number): string {
  const addOns = addOnCount === 1 ? "1 add-on" : `${addOnCount} add-ons`;

  return `Switching to Pay what you want will remove ${addOns}. ${ADD_ONS_UNAVAILABLE_UNDER_PWYW}`;
}

/**
 * What the Pricing section needs to know about an offering's existing Add-ons.
 *
 * Only the edit flow supplies one. In the create wizard the Pricing Model is chosen
 * several steps before Add-ons are ever offered, so there is nothing to destroy and no
 * provider to mount — hence the do-nothing default rather than a throwing one.
 */
export type ProductAddOnGate = {
  /** How many Add-ons the offering currently has. */
  addOnCount: number;
  /** Discards them. Called only once the coach has confirmed. */
  discardAddOns: () => Promise<void>;
};

const NO_EXISTING_ADD_ONS: ProductAddOnGate = {
  addOnCount: 0,
  discardAddOns: async () => {},
};

const ProductAddOnGateContext = createContext<ProductAddOnGate>(NO_EXISTING_ADD_ONS);

export function ProductAddOnGateProvider({
  gate,
  children,
}: PropsWithChildren<{gate: ProductAddOnGate}>) {
  return (
    <ProductAddOnGateContext.Provider value={gate}>
      {children}
    </ProductAddOnGateContext.Provider>
  );
}

export function useProductAddOnGate(): ProductAddOnGate {
  return useContext(ProductAddOnGateContext);
}
